/**
 * LifeScenario — Life OS 场景规划详情页
 *
 * 展示完整生活方案详情：
 * - 方案概览卡片（含 SVG 圆环评分）
 * - 垂直时间轴
 * - 预算分解条形图
 * - 天气适配卡片
 * - 风险提示列表
 * - 执行按钮组
 *
 * 设计风格：暗夜蓝 + 萤火暖金，极简 Apple 风格
 */

import { useState, useEffect } from 'react';

/* ═══════════════════════════════════════════
   Mock 数据
   ═══════════════════════════════════════════ */

const mockPlan = {
  title: '周六浪漫约会完整方案',
  summary: '月見·庭院日料 + 湖畔咖啡散步，预算¥500，预计3小时',
  score: 92,
  timeline: [
    { time: '18:00', action: '出发步行', detail: '步行15min到达餐厅', agent: 'route', icon: '🚶' },
    { time: '18:30', action: '月見·庭院日料', detail: '庭院景观位 · 主厨精选套餐 ¥185/人', agent: 'dining', icon: '🍣' },
    { time: '20:30', action: '湖畔散步', detail: '沿湖步行道欣赏夜景 (0.6km)', agent: 'route', icon: '🌙' },
    { time: '21:00', action: '湖畔咖啡', detail: '招牌手冲 ¥40/人', agent: 'dining', icon: '☕' },
    { time: '21:30', action: '结束', detail: '预计打车返程 ¥15', agent: 'route', icon: '🏠' },
  ],
  budget: {
    total: 500,
    items: [
      { label: '晚餐', value: 370, color: '#e8d28a' },
      { label: '咖啡', value: 80, color: '#a8c7d8' },
      { label: '交通', value: 50, color: '#7dd3fc' },
    ],
  },
  weather: {
    temp: '18-24°C',
    condition: '多云转晴',
    suggestion: '适合户外活动，建议携带薄外套',
  },
  risks: [
    { level: 'low', label: '安全', desc: '该区域治安良好' },
    { level: 'low', label: '天气', desc: '无降雨预警' },
    { level: 'medium', label: '拥挤', desc: '周末晚高峰建议提前到达' },
  ],
};

/* ═══════════════════════════════════════════
   工具函数
   ═══════════════════════════════════════════ */

const agentLabelMap: Record<string, string> = {
  route: '路径规划 Agent',
  dining: '美食推荐 Agent',
};

const riskColorMap: Record<string, string> = {
  low: '#4ade80',
  medium: '#fbbf24',
  high: '#f87171',
};

const riskLevelText: Record<string, string> = {
  low: '低风险',
  medium: '中风险',
  high: '高风险',
};

/* ═══════════════════════════════════════════
   子组件
   ═══════════════════════════════════════════ */

/** SVG 圆环评分 */
function ScoreRing({ score, size = 80 }: { score: number; size?: number }) {
  const strokeWidth = 5;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="rgba(255,255,255,0.06)"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--accent)"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 1.5s var(--ease)' }}
      />
    </svg>
  );
}

/* ═══════════════════════════════════════════
   主组件
   ═══════════════════════════════════════════ */

export default function LifeScenario() {
  const [animateBars, setAnimateBars] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const timer = setTimeout(() => setAnimateBars(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--night-950)',
        padding: '40px 24px 80px',
        fontFamily: 'var(--font-sans)',
      }}
    >
      {/* ── 顶部返回 —— */}
      <div style={{ maxWidth: 800, margin: '0 auto 24px' }}>
        <button
          style={{
            background: 'transparent',
            border: '1px solid var(--border-default)',
            borderRadius: 10,
            color: 'var(--text-secondary)',
            fontSize: 13,
            padding: '8px 16px',
            cursor: 'pointer',
            transition: 'border-color 200ms var(--ease), color 200ms var(--ease)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-hover)';
            e.currentTarget.style.color = 'var(--text-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-default)';
            e.currentTarget.style.color = 'var(--text-secondary)';
          }}
        >
          ← 返回方案列表
        </button>
      </div>

      {/* ── 方案概览卡片 ── */}
      <div
        style={{
          maxWidth: 800,
          margin: '0 auto 32px',
          background: 'var(--surface-1)',
          border: '1px solid var(--border-default)',
          borderRadius: 24,
          padding: '36px 40px',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          animation: mounted ? 'fadeUp 0.7s var(--ease) both' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24 }}>
          <div style={{ flex: 1 }}>
            {/* 标签 */}
            <div
              style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: 'var(--radius-pill)',
                background: 'var(--accent-soft)',
                color: 'var(--accent)',
                fontSize: 12,
                letterSpacing: '0.04em',
                marginBottom: 16,
              }}
            >
              LIFE OS · 场景规划
            </div>
            {/* 标题 */}
            <h1
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 'clamp(22px, 3vw, 28px)',
                fontWeight: 400,
                color: 'var(--text-primary)',
                lineHeight: 1.4,
                marginBottom: 12,
              }}
            >
              {mockPlan.title}
            </h1>
            {/* 摘要 */}
            <p
              style={{
                fontSize: 15,
                lineHeight: 1.7,
                color: 'var(--text-secondary)',
                margin: 0,
              }}
            >
              {mockPlan.summary}
            </p>
          </div>
          {/* 评分圆环 */}
          <div
            style={{
              position: 'relative',
              width: 80,
              height: 80,
              flexShrink: 0,
            }}
          >
            <ScoreRing score={mockPlan.score} />
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <span
                style={{
                  fontSize: 22,
                  fontWeight: 500,
                  color: 'var(--accent)',
                  lineHeight: 1,
                }}
              >
                {mockPlan.score}
              </span>
              <span
                style={{
                  fontSize: 10,
                  color: 'var(--text-muted)',
                  marginTop: 2,
                }}
              >
                推荐度
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── 两栏布局：时间轴 + 侧边 ── */}
      <div
        style={{
          maxWidth: 800,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 32,
        }}
      >
        {/* ── 时间轴 ── */}
        <div
          style={{
            background: 'var(--surface-1)',
            border: '1px solid var(--border-default)',
            borderRadius: 24,
            padding: '36px 40px',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            animation: mounted ? 'fadeUp 0.7s 0.1s var(--ease) both' : 'none',
          }}
        >
          <h2
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 18,
              fontWeight: 400,
              color: 'var(--text-primary)',
              marginBottom: 28,
            }}
          >
            执行流程
          </h2>

          <div style={{ position: 'relative', paddingLeft: 60 }}>
            {mockPlan.timeline.map((item, idx) => (
              <div
                key={idx}
                style={{
                  position: 'relative',
                  paddingBottom: idx === mockPlan.timeline.length - 1 ? 0 : 28,
                  borderLeft: idx === mockPlan.timeline.length - 1 ? 'none' : '2px solid rgba(255,255,255,0.06)',
                  marginLeft: 0,
                  paddingLeft: 28,
                  animation: mounted
                    ? `fadeUp 0.5s ${(0.2 + idx * 0.08).toFixed(2)}s var(--ease) both`
                    : 'none',
                }}
              >
                {/* 圆点 */}
                <div
                  style={{
                    position: 'absolute',
                    left: -7,
                    top: 2,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: 'var(--night-950)',
                    border: '2px solid var(--accent)',
                    boxShadow: '0 0 8px rgba(232, 210, 138, 0.3)',
                  }}
                />
                {/* 时间 */}
                <div
                  style={{
                    position: 'absolute',
                    left: -60,
                    top: 0,
                    width: 36,
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    textAlign: 'right',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {item.time}
                </div>
                {/* 内容 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span style={{ fontSize: 18 }}>{item.icon}</span>
                  <span
                    style={{
                      fontSize: 16,
                      fontWeight: 500,
                      color: 'var(--text-primary)',
                    }}
                  >
                    {item.action}
                  </span>
                </div>
                <p
                  style={{
                    fontSize: 13,
                    lineHeight: 1.6,
                    color: 'var(--text-secondary)',
                    margin: '0 0 8px',
                  }}
                >
                  {item.detail}
                </p>
                {/* Agent 标签 */}
                <span
                  style={{
                    display: 'inline-block',
                    padding: '3px 10px',
                    borderRadius: 'var(--radius-pill)',
                    background: 'var(--accent-soft)',
                    color: 'var(--accent-secondary)',
                    fontSize: 11,
                    letterSpacing: '0.02em',
                  }}
                >
                  {agentLabelMap[item.agent] || item.agent}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── 底部三栏：预算 + 天气 + 风险 ── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 24,
          }}
        >
          {/* ── 预算分解 ── */}
          <div
            style={{
              background: 'var(--surface-1)',
              border: '1px solid var(--border-default)',
              borderRadius: 24,
              padding: '28px 28px 24px',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              animation: mounted ? 'fadeUp 0.7s 0.3s var(--ease) both' : 'none',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                marginBottom: 20,
              }}
            >
              <h3
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 16,
                  fontWeight: 400,
                  color: 'var(--text-primary)',
                  margin: 0,
                }}
              >
                预算分解
              </h3>
              <span
                style={{
                  fontSize: 13,
                  color: 'var(--text-muted)',
                }}
              >
                总计 ¥{mockPlan.budget.total}
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {mockPlan.budget.items.map((item, idx) => {
                const pct = (item.value / mockPlan.budget.total) * 100;
                return (
                  <div key={idx}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 6,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 13,
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {item.label}
                      </span>
                      <span
                        style={{
                          fontSize: 13,
                          color: 'var(--text-secondary)',
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        ¥{item.value} ({Math.round(pct)}%)
                      </span>
                    </div>
                    <div
                      style={{
                        height: 6,
                        background: 'rgba(255,255,255,0.04)',
                        borderRadius: 3,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: animateBars ? `${pct}%` : '0%',
                          background: item.color,
                          borderRadius: 3,
                          transition: `width 1s ${(0.4 + idx * 0.15).toFixed(2)}s var(--ease)`,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── 天气卡片 ── */}
          <div
            style={{
              background: 'var(--surface-1)',
              border: '1px solid var(--border-default)',
              borderRadius: 24,
              padding: '28px 28px 24px',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              animation: mounted ? 'fadeUp 0.7s 0.35s var(--ease) both' : 'none',
            }}
          >
            <h3
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 16,
                fontWeight: 400,
                color: 'var(--text-primary)',
                marginBottom: 20,
                margin: '0 0 20px',
              }}
            >
              天气适配
            </h3>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 36,
                  lineHeight: 1,
                }}
              >
                ⛅
              </div>
              <div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 500,
                    color: 'var(--text-primary)',
                    fontFamily: 'var(--font-mono)',
                    lineHeight: 1.3,
                  }}
                >
                  {mockPlan.weather.temp}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--text-secondary)',
                    marginTop: 2,
                  }}
                >
                  {mockPlan.weather.condition}
                </div>
              </div>
            </div>

            <div
              style={{
                padding: '12px 14px',
                borderRadius: 12,
                background: 'rgba(168, 199, 216, 0.06)',
                border: '1px solid rgba(168, 199, 216, 0.12)',
              }}
            >
              <p
                style={{
                  fontSize: 13,
                  color: 'var(--accent-secondary)',
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                {mockPlan.weather.suggestion}
              </p>
            </div>
          </div>
        </div>

        {/* ── 风险提示 ── */}
        <div
          style={{
            background: 'var(--surface-1)',
            border: '1px solid var(--border-default)',
            borderRadius: 24,
            padding: '28px 28px 24px',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            animation: mounted ? 'fadeUp 0.7s 0.45s var(--ease) both' : 'none',
          }}
        >
          <h3
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 16,
              fontWeight: 400,
              color: 'var(--text-primary)',
              margin: '0 0 16px',
            }}
          >
            风险提示
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {mockPlan.risks.map((risk, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '12px 16px',
                  borderRadius: 12,
                  background: 'rgba(255,255,255,0.02)',
                  borderLeft: `3px solid ${riskColorMap[risk.level]}`,
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: riskColorMap[risk.level],
                    flexShrink: 0,
                    boxShadow: `0 0 6px ${riskColorMap[risk.level]}40`,
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 2,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: 'var(--text-primary)',
                      }}
                    >
                      {risk.label}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        padding: '1px 8px',
                        borderRadius: 'var(--radius-pill)',
                        background: `${riskColorMap[risk.level]}18`,
                        color: riskColorMap[risk.level],
                      }}
                    >
                      {riskLevelText[risk.level]}
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: 'var(--text-secondary)',
                      margin: 0,
                      lineHeight: 1.5,
                    }}
                  >
                    {risk.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── 执行按钮组 ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            animation: mounted ? 'fadeUp 0.7s 0.55s var(--ease) both' : 'none',
          }}
        >
          <button
            style={{
              flex: 1,
              height: 48,
              border: 'none',
              borderRadius: 'var(--radius-pill)',
              background: 'var(--accent)',
              color: 'var(--night-900)',
              fontSize: 15,
              fontWeight: 500,
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(232, 210, 138, 0.25)',
              transition: 'opacity 200ms var(--ease), transform 200ms var(--ease)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            立即预约
          </button>
          <button
            style={{
              height: 48,
              padding: '0 28px',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-pill)',
              background: 'rgba(255,255,255,0.04)',
              color: 'var(--text-primary)',
              fontSize: 14,
              cursor: 'pointer',
              transition: 'border-color 200ms var(--ease), background 200ms var(--ease)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-hover)';
              e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-default)';
              e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
            }}
          >
            保存方案
          </button>
          <button
            style={{
              height: 48,
              padding: '0 28px',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-pill)',
              background: 'rgba(255,255,255,0.04)',
              color: 'var(--text-primary)',
              fontSize: 14,
              cursor: 'pointer',
              transition: 'border-color 200ms var(--ease), background 200ms var(--ease)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-hover)';
              e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-default)';
              e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
            }}
          >
            分享方案
          </button>
        </div>
      </div>
    </div>
  );
}
